import { mxGraph, mxEvent, mxConstants, mxPoint, mxRectangle, mxUtils, MxGraphNS, mxGeometry } from "@edp-pmf/mxgraph-ts";
import { EndEvent, BpmnModel, Process, BPMNDiagram, FlowElement, DiagramElement, BPMNShape, BPMNEdge, SequenceFlow, FlowNode, Node, Edge, FlowNodeState, SequenceFlowState } from "@edp-pmf/bpmn-model";
import { StartActivity, UserActivity, CallActivitySubProcess, AutoActivity, IntermediateCatchEventImpl, OperationActivity, MessageActivity, NotifyActivity, AifCreationBillActivity } from "@gsp-wf/wf-process-model";
import { Cell } from "./entity/Cell";
import { ActivityInstanceSlim } from "./entity/ActivityInstanceSlim";
import { TransitionInstanceSlim } from "./entity/TransitionInstanceSlim";
import { CellStyleConfig } from "../entity/cell-style/CellStyleConfig";
import { StateShapeStyle } from "../entity/cell-style/StateShapeStyle";

export class FlowGraph extends mxGraph {
    bpmnModel: BpmnModel;
    autoTranslate: boolean;
    scrollTileSize: MxGraphNS.mxRectangle = new mxRectangle(0, 0, 400, 600);
    getPagePadding: () => MxGraphNS.mxPoint;
    getPageSize: () => MxGraphNS.mxRectangle;
    getScrollTileSize: () => MxGraphNS.mxRectangle;
    getPageLayout: () => MxGraphNS.mxRectangle;

    cellStyleConfig: CellStyleConfig = {
        edgeStyleDic: {},
        shapeStyleDic: {},
    };

    actList: ActivityInstanceSlim[] = [];
    transInsList: TransitionInstanceSlim[] = [];
    mode: string;
    rootPath: string = '';//基路径
    /**
     * 当前语言
     */
    langCode = (window as any).gspframeworkService && (window as any).gspframeworkService.rtf.language.getLanguageCode() || localStorage.getItem('languageCode') || 'zh-CHS';

    constructor(container: HTMLDivElement, cellStyleConfig?: CellStyleConfig) {
        super(container);
        //获取基路径
        try {
            this.rootPath = (window as any).gspframeworkService.common.getBasePath.get();
        } catch (error) {
        }
        this.cellStyleConfig = cellStyleConfig ? cellStyleConfig : this.cellStyleConfig;
        this.autoScroll = false;
        this.autoExtend = true;
        this.pageVisible = false;
        //this.panningHandler.ignoreCell = true;
        //this.setPanning(true);
        this.setCellsSelectable(true);
        this.setDisconnectOnMove(false);
        //节点不可改变大小
        this.setCellsResizable(false);
        //cell是否可以连线
        this.setConnectable(false);
        //禁止操作
        this.setEnabled(true);
        //设置cell文本是否可移动
        this.setVertexLabelsMovable(false);

        this.setCellsLocked(true);
        //不允许图上存在没有连接活动节点的线（防止拖动线连接点导致失去连接）,还没有找到不允许拖动连接控制点的属性
        this.setAllowDanglingEdges(false);
        mxEvent.disableContextMenu(container);

        // 设置默认样式
        this.setDefaultStyles();
    }

    /**
     * 重写创建节点对象方法
     * @param parent
     * @param id
     * @param value
     * @param x
     * @param y
     * @param width
     * @param height
     * @param style
     * @param relative
     * @returns
     */
    createVertex(parent, id, value, x, y, width, height, style, relative) {
        // Creates the geometry for the vertex
        const geometry = new mxGeometry(x, y, width, height);
        geometry.relative = (relative != null) ? relative : false;

        // Creates the vertex
        const vertex = new Cell(value, geometry, style);
        vertex.setId(id);
        vertex.setVertex(true);
        vertex.setConnectable(true);

        return vertex;
    }

    /**
     * 重写创建边对象方法
     * @param parent
     * @param id
     * @param value
     * @param source
     * @param target
     * @param style
     * @returns
     */
    createEdge(parent, id, value, source, target, style) {
        // Creates the edge
        var edge = new Cell(value, new mxGeometry(), style);
        edge.setId(id);
        edge.setEdge(true);
        edge.geometry.relative = true;

        return edge;
    }

    /**
     * 设置默认样式
     */
    setDefaultStyles() {
        const styleSheet = this.getStylesheet();
        // 节点默认样式
        const vertexStyle = styleSheet.getDefaultVertexStyle();
        vertexStyle[mxConstants.STYLE_SHAPE] = mxConstants.SHAPE_IMAGE;
        vertexStyle[mxConstants.STYLE_VERTICAL_LABEL_POSITION] = mxConstants.ALIGN_BOTTOM;
        vertexStyle[mxConstants.STYLE_FONTSIZE] = 12;
        vertexStyle[mxConstants.STYLE_FONTFAMILY] = 'Helvetica';
        vertexStyle[mxConstants.STYLE_FONTCOLOR] = "#333";

        // 连线样式默认为正交
        const edgeStyle = styleSheet.getDefaultEdgeStyle();
        edgeStyle[mxConstants.STYLE_SOURCE_PORT] = 'e';
        edgeStyle[mxConstants.STYLE_TARGET_PORT] = 'w';
        edgeStyle[mxConstants.STYLE_EDGE] = mxConstants.EDGESTYLE_ORTHOGONAL;
        edgeStyle[mxConstants.STYLE_ROUNDED] = 1;
        edgeStyle[mxConstants.STYLE_STROKECOLOR] = '#6482B9';
        edgeStyle[mxConstants.STYLE_FONTSIZE] = 12;
        edgeStyle[mxConstants.STYLE_FONTFAMILY] = 'Helvetica';
    }

    /**
     * 创建无限大画布
     */
    createInfiniteCanvas() {
        this.getPagePadding = () => {
            return new mxPoint(
                Math.max(0, Math.round(this.container.offsetWidth - 34)),
                Math.max(0, Math.round(this.container.offsetHeight - 34)));
        }

        this.getScrollTileSize = () => {
            if (this.scrollTileSize == null) {
                this.scrollTileSize = new mxRectangle(0, 0, 400, 600);
            }

            return this.scrollTileSize;
        }

        this.getPageSize = () => {
            return (this.pageVisible) ? new mxRectangle(0, 0, this.pageFormat.width * this.pageScale, this.pageFormat.height * this.pageScale) : this.getScrollTileSize();
        }

        this.getPageLayout = () => {
            let size = (this.pageVisible) ? this.getPageSize() : this.getScrollTileSize();
            let bounds = this.getGraphBounds();

            if (bounds.width == 0 || bounds.height == 0) {
                return new mxRectangle(0, 0, 1, 1);
            }
            else {
                // Computes untransformed graph bounds
                let x = Math.ceil(bounds.x / this.view.scale - this.view.translate.x);
                let y = Math.ceil(bounds.y / this.view.scale - this.view.translate.y);
                let w = Math.floor(bounds.width / this.view.scale);
                let h = Math.floor(bounds.height / this.view.scale);

                let x0 = Math.floor(x / size.width);
                let y0 = Math.floor(y / size.height);
                let w0 = Math.ceil((x + w) / size.width) - x0;
                let h0 = Math.ceil((y + h) / size.height) - y0;

                return new mxRectangle(x0, y0, w0, h0);
            }
        }

        this.getPreferredPageSize = (bounds, width, height) => {
            let pages = this.getPageLayout();
            let size = this.getPageSize();

            return new mxRectangle(0, 0, pages.width * size.width, pages.height * size.height);
        };

        // 重写画布大小变化后方法
        const graphSizeDidChange = this.sizeDidChange;
        this.sizeDidChange = () => {
            if (this.container != null && mxUtils.hasScrollbars(this.container)) {
                let pages = this.getPageLayout();
                let pad = this.getPagePadding();
                let size = this.getPageSize();

                // Updates the minimum graph size
                let minw = Math.ceil(2 * pad.x / this.view.scale + pages.width * size.width);
                let minh = Math.ceil(2 * pad.y / this.view.scale + pages.height * size.height);

                let min = this.minimumGraphSize;

                // LATER: Fix flicker of scrollbar size in IE quirks mode
                // after delayed call in window.resize event handler
                if (min == null || min.width != minw || min.height != minh) {
                    this.minimumGraphSize = new mxRectangle(0, 0, minw, minh);
                }

                // Updates auto-translate to include padding and graph size
                let dx = pad.x / this.view.scale - pages.x * size.width;
                let dy = pad.y / this.view.scale - pages.y * size.height;

                if (!this.autoTranslate && (this.view.translate.x != dx || this.view.translate.y != dy)) {
                    this.autoTranslate = true;
                    (this.view as any).x0 = pages.x;
                    (this.view as any).y0 = pages.y;

                    let tx = this.view.translate.x;
                    let ty = this.view.translate.y;

                    this.view.setTranslate(dx, dy);
                    this.container.scrollLeft += (dx - tx) * this.view.scale;
                    this.container.scrollTop += (dy - ty) * this.view.scale;

                    this.autoTranslate = false;
                    return;
                }

                graphSizeDidChange.apply(this, []);
            }
        };

        // 重写view获取背景页大小
        this.view.getBackgroundPageBounds = () => {
            let layout = this.getPageLayout();
            let page = this.getPageSize();

            return new mxRectangle(
                this.view.scale * (this.view.translate.x + layout.x * page.width),
                this.view.scale * (this.view.translate.y + layout.y * page.height),
                this.view.scale * layout.width * page.width,
                this.view.scale * layout.height * page.height
            );

        };

        // 重写view validate方法
        const graphViewValidate = this.view.validate;
        this.view.validate = (cell?: MxGraphNS.mxCell) => {
            if (this.container != null && mxUtils.hasScrollbars(this.container)) {
                let pad = this.getPagePadding();
                let size = this.getPageSize();
                let tx = this.view.translate.x;
                let ty = this.view.translate.y;
                this.view.translate.x = pad.x / this.view.scale - ((this.view as any).x0 || 0) * size.width;
                this.view.translate.y = pad.y / this.view.scale - ((this.view as any).y0 || 0) * size.height;
            }

            graphViewValidate.apply(this.view, [cell]);
        };
    }


    /**
     * 创建BpmnModel
     */
    createBpmnModel() {
        const bpmnModel = new BpmnModel();
        bpmnModel.TargetNamespace = "Default_Namespace";
        bpmnModel.DefaultProcess = new Process(bpmnModel);
        bpmnModel.DefaultDiagram = new BPMNDiagram(bpmnModel);
        bpmnModel.DefaultDiagram.BpmnPlane.BpmnElementId = bpmnModel.DefaultProcess.Id;

        return bpmnModel;
    }

    /**
     * 加载BpmnModel并画图
     */
    loadBpmnModel(json: string) {
        this.removeCells(this.getChildCells(this.getDefaultParent()));
        this.bpmnModel = this.createBpmnModel();
        this.bpmnModel.LoadFromJson(json);
        this.drawBpmnGraph(this.bpmnModel);
    }

    /**
     * 重新加载BpmnModel并画图
     * @param json
     */
    reloadBpmnModel(json?: string) {
        this.removeCells(this.getChildCells(this.getDefaultParent()));
        if (json) {
            this.loadBpmnModel(json);
        } else {
            this.drawBpmnGraph(this.bpmnModel);
        }
    }

    /**
     * 根据BpmnModel画图
     */
    drawBpmnGraph(bpmnModel: BpmnModel) {
        const { nodeDict, edgeDict } = this.buildFlowDiagDict(bpmnModel);
        const flowDict = this.buildFlowDict(bpmnModel);
        this.drawNodeCells(flowDict, nodeDict);
        this.drawEdgeCells(flowDict, edgeDict);
        this.getModel().beginUpdate();
        if (this.mode === 'simulation') {
            this.drawInterceptGraph(flowDict, nodeDict, edgeDict);
            this.addJumpEdges(flowDict);
        }
        this.getModel().endUpdate();
    }

    private drawInterceptGraph(flowDict: { [key: string]: FlowElement; },
        nodeDict: { [BpmnElementId: string]: DiagramElement; },
        edgeDict: { [BpmnElementId: string]: DiagramElement; }) {
        const firstAct = this.findFirstStartActivityInstance(this.actList);
        // todo：此处待修复
        let endActDef: EndEvent;
        for (const key in flowDict) {
            if (flowDict[key] instanceof EndEvent) {
                endActDef = flowDict[key] as EndEvent;
            }
        }
        if (firstAct) {
            const endActiInstList = this.findEndActiInstList(firstAct, this.actList, this.transInsList);
            endActiInstList.forEach((endActiInst => {
                if (endActiInst.activityDefinitionId !== endActDef.Id) {
                    this.drawNextFlowChart(endActiInst.activityDefinitionId, nodeDict, edgeDict);
                }
            }));
        }
    }

    addJumpEdges(flowDict: { [key: string]: FlowElement; }) {
        const nodeCells = (this.getChildCells(this.getDefaultParent(), true) || []) as Cell[];
        const noIncomingNodes: Cell[] = this.findNoIncomingNodes(nodeCells);
        for (const node of noIncomingNodes) {
            const actInst = this.actList.filter(a => a.activityDefinitionId === node.flowElement.Id)[0];
            const cellId = actInst.precursorActivityDefinitionId + '_' + actInst.activityDefinitionId;
            const source = nodeCells.find((it) => {
                return it.flowElement && it.flowElement.Id === actInst.precursorActivityDefinitionId;
            });
            const target = nodeCells.find((it) => {
                return it.flowElement && it.flowElement.Id === actInst.activityDefinitionId;
            });
            const map = new Map();
            map.set('jump', true);
            const style = this.getEdgeStyle(null, null, map);
            const cell = this.insertEdge(this.getDefaultParent(), cellId, '', source, target, style) as Cell;
            if (cell !== null) {
                cell.source = source;
                cell.target = target;
                cell.geometry.points = [
                    // new mxPoint(source.geometry.x+ source.geometry.width/2, target.geometry.y+target.geometry.height/2)
                ];
                console.log(cell);
                this.moveCells([cell], undefined, undefined, undefined, this.getDefaultParent());
            }
        }
    }

    /**
     * 画节点
     */
    drawNodeCells(
        flowDict: { [key: string]: FlowElement; },
        nodeDict: { [BpmnElementId: string]: DiagramElement; },
        additional: boolean = false,
    ) {
        Object.keys(nodeDict).forEach((bpmnModelElementId: string) => {
            const flowElt = flowDict[bpmnModelElementId];
            const diagElt = nodeDict[bpmnModelElementId] as BPMNShape;
            if (flowElt == null) {
                return;
            }

            this.drawNodeCell(flowElt, diagElt, additional);
        });
    }

    /**
     * 画单个节点
     * @param flowElt
     * @param diagElt
     * @param additional
     */
    drawNodeCell(flowElt: FlowElement, diagElt: BPMNShape, additional: boolean) {
        const style = this.getVertexStyle(flowElt, diagElt, additional);
        let cell: Cell = null;
        let label = "";
        if (flowElt != null) {
            label = flowElt.Name;
            if (flowElt.NameLanguage && flowElt.NameLanguage[this.langCode] != null) {
                label = flowElt.NameLanguage[this.langCode];
            }
        }
        if (this.mode === 'simulation') {
            if (this.actList.findIndex(a => a.activityDefinitionId === flowElt.Id) > -1) {
                cell = this.insertVertex(this.getDefaultParent(), flowElt.Id, label, diagElt.Bounds.X, diagElt.Bounds.Y, diagElt.Bounds.Width, diagElt.Bounds.Height, style) as Cell;
            } else if (additional) {
                cell = this.insertVertex(this.getDefaultParent(), flowElt.Id, label, diagElt.Bounds.X, diagElt.Bounds.Y, diagElt.Bounds.Width, diagElt.Bounds.Height, style) as Cell;
            }
        } else {
            cell = this.insertVertex(this.getDefaultParent(), flowElt.Id, label, diagElt.Bounds.X, diagElt.Bounds.Y, diagElt.Bounds.Width, diagElt.Bounds.Height, style) as Cell;
        }
        if (cell == null) {
            return;
        }
        // const cell = this.insertVertex(this.getDefaultParent(), flowElt.Id, label, diagElt.Bounds.X, diagElt.Bounds.Y, diagElt.Bounds.Width, diagElt.Bounds.Height, style) as Cell;
        cell.flowElement = flowElt;
        cell.diagramElement = diagElt;
        this.moveCells([cell], undefined, undefined, undefined, this.getDefaultParent());
    }

    /**
     * 画连线
     */
    drawEdgeCells(
        flowDict: { [key: string]: FlowElement; },
        edgeDict: { [BpmnElementId: string]: DiagramElement; },
        additional: boolean = false,
        cut: boolean = false,
    ) {
        Object.keys(edgeDict).forEach((bpmnModelElementId: string) => {
            const flowElt = flowDict[bpmnModelElementId] as SequenceFlow;
            const diagElt = edgeDict[bpmnModelElementId] as BPMNEdge;

            if (flowElt == null) {
                return;
            }

            this.drawEdgeCell(flowElt, diagElt, additional, cut);
        });
    }




    private judgeConnectBetweenTwoNodes(flowDict: { [key: string]: FlowElement; }, sourceId: string, targetId: string): boolean {
        const source = flowDict[sourceId] as FlowNode;
        const outGoings = source.Outgoings;
        if (!outGoings || outGoings.length === 0) {
            return false;
        }
        for (const outGoing of outGoings) {
            const sequenceFlow = flowDict[outGoing] as SequenceFlow;
            if (sequenceFlow.TargetRef === targetId) {
                return true;
            }
        }
        return false;
    }

    /**
     * 画单条连线
     * @param flowElt
     * @param diagElt
     * @param additional
     * @param cut
     * @returns
     */
    drawEdgeCell(flowElt: SequenceFlow, diagElt: BPMNEdge, additional: boolean, cut: boolean) {
        // 获取已插入的节点元素
        const nodeCells = (this.getChildCells(this.getDefaultParent(), true) || []) as Cell[];
        const source = nodeCells.find((it) => {
            return it.flowElement && it.flowElement.Id === flowElt.SourceRef;
        });
        const target = nodeCells.find((it) => {
            return it.flowElement && it.flowElement.Id === flowElt.TargetRef;
        });
        const map = new Map();
        map.set('additional', additional);
        map.set('cut', cut);
        const style = this.getEdgeStyle(flowElt, diagElt, map);
        let cell: Cell = null;
        let label = "";
        if (flowElt != null) {
            label = flowElt.Name;
            if (flowElt.NameLanguage && flowElt.NameLanguage[this.langCode] != null) {
                label = flowElt.NameLanguage[this.langCode];
            }
        }
        if (this.mode === 'simulation') {
            if (this.transInsList.findIndex(a => a.sourceActivityDefinitionId === flowElt.SourceRef
                && a.destinationActivityDefinitionId === flowElt.TargetRef) > -1) {
                cell = this.insertEdge(this.getDefaultParent(), flowElt.Id, label, source, target, style) as Cell;
            } else if (additional || cut) {
                cell = this.insertEdge(this.getDefaultParent(), flowElt.Id, label, source, target, style) as Cell;
            }
        } else {
            cell = this.insertEdge(this.getDefaultParent(), flowElt.Id, label, source, target, style) as Cell;
        }

        if (cell == null) {
            return;
        }
        // const cell = this.insertEdge(this.getDefaultParent(), flowElt.Id, label, source, target, style) as Cell;
        // cell.geometry.relative = true;
        cell.flowElement = flowElt;
        cell.diagramElement = diagElt;
        cell.source = source;
        cell.target = target;

        cell.geometry.points = cell.geometry.points || [];

        // 判断是否为旧版本连线
        if (this.isOldBpmnEdge(diagElt)) {
            for (let i = 2; i < diagElt.Points.length; i++) {
                const pt = diagElt.Points[i];
                cell.geometry.points.push(new mxPoint(pt.X, pt.Y));
            }

            // 此行代码执行后可获取mxCellState
            this.moveCells([cell], undefined, undefined, undefined, this.getDefaultParent());

            //按坐标插入label
            const state = this.view.getState(cell);
            if (diagElt.BpmnLabel && state) {
                if (diagElt.BpmnLabel.Bounds.X === 0 && diagElt.BpmnLabel.Bounds.Y === 0) {
                    return;
                }
                const x = diagElt.BpmnLabel.Bounds.X + (state.x - state.paintBounds.x);
                const y = diagElt.BpmnLabel.Bounds.Y + (state.y - state.paintBounds.y);

                const edgeHandler: MxGraphNS.mxEdgeHandler = this.createEdgeHandler(state, undefined);
                edgeHandler.moveLabel(state, x, y);
                edgeHandler.destroy();
            }
        } else {
            for (let i = 1; i < diagElt.Points.length - 1; i++) {
                const pt = diagElt.Points[i];
                cell.geometry.points.push(new mxPoint(pt.X, pt.Y));
            }

            // 此行代码执行后可获取mxCellState
            this.moveCells([cell], undefined, undefined, undefined, this.getDefaultParent());

            //按坐标插入label
            const state = this.view.getState(cell);
            const scale = this.view.getScale();
            const translate = this.view.getTranslate();
            if (diagElt.BpmnLabel && state) {
                if (diagElt.BpmnLabel.Bounds.X === 0 && diagElt.BpmnLabel.Bounds.Y === 0) {
                    return;
                }
                const x = diagElt.BpmnLabel.Bounds.X * scale + translate.x;
                const y = diagElt.BpmnLabel.Bounds.Y * scale + translate.y;

                const edgeHandler: MxGraphNS.mxEdgeHandler = this.createEdgeHandler(state, undefined);
                edgeHandler.moveLabel(state, x, y);
                edgeHandler.destroy();
            }
        }
    }

    /**
     * 构建FlowElement字典，{ [flowEltId: string]: FlowElement }
     */
    buildFlowDict(bpmnModel: BpmnModel) {
        let flowDict = bpmnModel.DefaultProcess.GetFlowElements();

        return flowDict;
    }

    /**
     * 构建DiagramElement字典，{ [flowEltId: string]: DiagramElement }
     */
    buildFlowDiagDict(bpmnModel: BpmnModel) {
        const nodeDict: { [BpmnElementId: string]: DiagramElement; } = {};
        const edgeDict: { [BpmnElementId: string]: DiagramElement; } = {};

        const diagramElts: (DiagramElement & { BpmnElementId: string; })[] = bpmnModel.DefaultDiagram.BpmnPlane.GetDiagramElements() as any;

        diagramElts && diagramElts.forEach(elt => {
            if (elt instanceof Node) {
                nodeDict[elt.BpmnElementId] = elt;
            } else if (elt instanceof Edge) {
                edgeDict[elt.BpmnElementId] = elt;
            }
        });

        return {
            nodeDict,
            edgeDict,
        }
    }

    /**
     * 判断是否为旧版本BPMNEdge
     *
     * 旧版本连线Points数组中[0]和[1]分别表示起点和终点，因取值错误，[0]和[1]坐标均为(0,0)，以此为依据判断是否为旧版本的连线
     * 新版本从起点到终点按顺序记录每一个点坐标
     * @param bpmnEdge
     * @returns
     */
    isOldBpmnEdge(bpmnEdge: BPMNEdge) {
        if (bpmnEdge.Points.length >= 2) {
            const pt1 = bpmnEdge.Points[0];
            const pt2 = bpmnEdge.Points[1];
            if (pt1.X == 0 && pt1.Y == 0 && pt2.X == 0 && pt2.Y == 0) {
                return true;
            }
        }
        return false;
    }

    /**
     * 获取节点样式
     * @param flowElt
     * @param diagElt
     * @param additional
     * @returns
     */
    getVertexStyle(flowElt: FlowElement, diagElt: BPMNShape, additional: boolean) {
        const activityInstanceSlims = this.actList;

        let stateShapeStyle: StateShapeStyle = null;
        // 特殊处理,业务活动要单独赋成人工操作类型的样式
        // if (flowElt.ClrTypeID === UserActivity.Clr_Type_ID) {
        //   stateShapeStyle = this.cellStyleConfig.shapeStyleDic && this.cellStyleConfig.shapeStyleDic[flowElt.ClrTypeID];
        // } else if (flowElt.ClrTypeID === OperationActivity.Clr_Type_ID) {
        //   stateShapeStyle = this.cellStyleConfig.shapeStyleDic && this.cellStyleConfig.shapeStyleDic[OperationActivity.Clr_Type_ID];
        // }
        if (flowElt instanceof UserActivity && flowElt.BizActivityID !== '') {
            stateShapeStyle = this.cellStyleConfig.shapeStyleDic && this.cellStyleConfig.shapeStyleDic[OperationActivity.Clr_Type_ID];
        } else {
            stateShapeStyle = this.cellStyleConfig.shapeStyleDic && this.cellStyleConfig.shapeStyleDic[flowElt.ClrTypeID];
        }
        if (stateShapeStyle == null) {
            return this.getVertexStyleOld(flowElt, additional);
        }
        let img: string = stateShapeStyle[FlowNodeState.Default] && stateShapeStyle[FlowNodeState.Default].image;
        if (!additional) {
            const acts = activityInstanceSlims.filter(item => item.activityDefinitionId === flowElt.Id);
            if (acts && acts.length > 0) {
                if (acts.filter(act => act.state === 'RUNNING').length > 0) {
                    if (stateShapeStyle[FlowNodeState.Running]) {
                        img = stateShapeStyle[FlowNodeState.Running].image;
                    }
                } else if (acts.filter(act => act.state === 'COMPLETED').length > 0) {
                    if (stateShapeStyle[FlowNodeState.Completed]) {
                        img = stateShapeStyle[FlowNodeState.Completed].image;
                    }
                } else if (acts.filter(act => act.state === 'TERMINATED').length > 0) {
                    if (stateShapeStyle[FlowNodeState.Terminated]) {
                        img = stateShapeStyle[FlowNodeState.Terminated].image;
                    }
                }
            }
        }
        return img == null ? null : 'image=' + this.rootPath + img;
    }

    /**
     * 旧版diagramElement未存储样式信息
     * @param flowElt
     * @param additional
     * @returns
     */
    getVertexStyleOld(flowElt: FlowElement, additional: boolean) {
        let style: string;
        switch (flowElt.ClrTypeID) {
            //结束事件
            case "bpmn.EndEvent":
                style = 'end';
                break;
            //流程开始活动节点
            case StartActivity.Clr_Type_ID:
                style = 'start';
                break;
            // 业务活动节点
            case UserActivity.Clr_Type_ID:
                if ((flowElt as UserActivity).BizActivityID) {
                    style = 'bizActivity';
                } else {
                    style = 'approval';
                }
                break;
            // 排他网关
            case 'bpmn.ExclusiveGateway':
                style = 'ExclusiveGateway';
                break;
            //并行网关
            case 'bpmn.ParallelGateway':
                style = 'ParallelGateway';
                break;
            case CallActivitySubProcess.Clr_Type_ID:
                style = 'sub';
                break;
            case AutoActivity.Clr_Type_ID:
                style = 'auto';
                break;
            case IntermediateCatchEventImpl.Clr_Type_ID:
                style = 'catchEvent';
                break;
            case OperationActivity.Clr_Type_ID:
                style = 'bizActivity';
                break;
            case MessageActivity.Clr_Type_ID:
                style = 'message';
                break;
            case NotifyActivity.Clr_Type_ID:
                style = 'notify';
                break;
            case AifCreationBillActivity.Clr_Type_ID:
                style = 'aifCreationActivity';
                break;
            default:
                break;
        }
        return 'image=' + this.rootPath + '/platform/runtime/common/web/@gsp-wf/wf-process-editor/images/' + style + this.getChartState(flowElt, additional) + ';';
    }

    getChartState(flowElt: FlowElement, additional: boolean) {
        if (!additional) {
            const acts = this.actList.filter(item => item.activityDefinitionId === flowElt.Id);
            if (acts && acts.length > 0) {
                if (acts.filter(act => act.state === 'RUNNING').length > 0) {
                    return '_light.gif';
                } else if (acts.filter(act => act.state === 'COMPLETED').length > 0
                    || acts.filter(act => act.state === 'TERMINATED').length > 0
                ) {
                    return '_gray.png';
                } else {
                    return '.png';
                }
            }
        }
        return '.png';
    }

    /**
     * 获取连线样式
     * @param flowElt
     * @param additional
     * @param cut
     * @returns
     */
    getEdgeStyle(flowElt: SequenceFlow, diagElt: BPMNEdge, parameters: Map<string, boolean>) {
        if (parameters.get('jump')) {
            let style = '';
            const stateEdgeStyle = this.cellStyleConfig.edgeStyleDic && this.cellStyleConfig.edgeStyleDic['bpmn.SequenceFlow'];
            if (stateEdgeStyle['jump']) {
                Object.keys(stateEdgeStyle['jump']).forEach((key) => {
                    style += (`${key}=${stateEdgeStyle['jump'][key]};`);
                });
                return style;
            }
            return null;
        } else {
            const stateEdgeStyle = this.cellStyleConfig.edgeStyleDic && this.cellStyleConfig.edgeStyleDic[flowElt.ClrTypeID];
            if (stateEdgeStyle == null) {
                return this.getEdgeStyleOld(flowElt, parameters.get('additional'), parameters.get('cut'));
            }
            const transitionInstanceSlims = this.transInsList;
            let style = '';
            if (parameters.get('cut')) {
                if (stateEdgeStyle[SequenceFlowState.Cut]) {
                    Object.keys(stateEdgeStyle[SequenceFlowState.Cut]).forEach((key) => {
                        style += (`${key}=${stateEdgeStyle[SequenceFlowState.Cut][key]};`);
                    });
                    return style;
                }
            }
            if (parameters.get('additional')) {
                if (stateEdgeStyle[SequenceFlowState.Add]) {
                    Object.keys(stateEdgeStyle[SequenceFlowState.Add]).forEach((key) => {
                        style += (`${key}=${stateEdgeStyle[SequenceFlowState.Add][key]};`);
                    });
                    return style;
                }
            }
            const transIns = transitionInstanceSlims.filter(item => item.sourceActivityDefinitionId === flowElt.SourceRef && item.destinationActivityDefinitionId === flowElt.TargetRef);
            if (transIns && transIns.filter(t => t.state === 'COMPLETED').length > 0) {
                if (stateEdgeStyle[SequenceFlowState.Completed]) {
                    Object.keys(stateEdgeStyle[SequenceFlowState.Completed]).forEach((key) => {
                        style += (`${key}=${stateEdgeStyle[SequenceFlowState.Completed][key]};`);
                    });
                    return style;
                }
            }
            if (this.mode === 'simulation') {
                if (stateEdgeStyle[SequenceFlowState.Simulation]) {
                    Object.keys(stateEdgeStyle[SequenceFlowState.Simulation]).forEach((key) => {
                        style += (`${key}=${stateEdgeStyle[SequenceFlowState.Simulation][key]};`);
                    });
                    return style;
                }
            }
            if (stateEdgeStyle[SequenceFlowState.Default]) {
                Object.keys(stateEdgeStyle[SequenceFlowState.Default]).forEach((key) => {
                    style += (`${key}=${stateEdgeStyle[SequenceFlowState.Default][key]};`);
                });
            }
            return style;
        }
    }

    /**
     * 获取连线颜色
     * @param flowElt
     * @param diagElt
     * @param additional
     * @param cut
     * @returns
     */
    getStrokeColor(flowElt: SequenceFlow, diagElt: BPMNEdge, additional: boolean, cut: boolean) {
        const transitionInstanceSlims = this.transInsList;

        const stateEdgeStyle = this.cellStyleConfig.edgeStyleDic && this.cellStyleConfig.edgeStyleDic[flowElt.ClrTypeID];


        if (cut) {
            if (stateEdgeStyle[SequenceFlowState.Cut]) {
                return stateEdgeStyle[SequenceFlowState.Cut].strokeColor;
            }
        }
        if (additional) {
            if (stateEdgeStyle[SequenceFlowState.Add]) {
                return stateEdgeStyle[SequenceFlowState.Add].strokeColor;
            }
        }
        const transIns = transitionInstanceSlims.filter(item => item.sourceActivityDefinitionId === flowElt.SourceRef && item.destinationActivityDefinitionId === flowElt.TargetRef);
        if (transIns && transIns.filter(t => t.state === 'COMPLETED').length > 0) {
            if (stateEdgeStyle[SequenceFlowState.Completed]) {
                return stateEdgeStyle[SequenceFlowState.Completed].strokeColor;
            }
        }
        if (this.mode === 'simulation') {
            if (stateEdgeStyle[SequenceFlowState.Simulation]) {
                return stateEdgeStyle[SequenceFlowState.Simulation].strokeColor;
            }
        }
        return stateEdgeStyle[SequenceFlowState.Default].strokeColor;
    }

    /**
     * 旧版diagramElement未存储样式信息
     * @param flowElt
     * @param additional
     * @returns
     */
    getEdgeStyleOld(seq: SequenceFlow, additional: boolean, cut: boolean) {
        if (cut) {
            return 'strokeColor=#FF4040'
        }
        if (additional) {
            return 'strokeColor=#59A1FF';
        }
        const transIns = this.transInsList.filter(item => item.sourceActivityDefinitionId === seq.SourceRef && item.destinationActivityDefinitionId === seq.TargetRef);
        if (transIns && transIns.filter(t => t.state === 'COMPLETED').length > 0) {
            return 'strokeColor=#595959;strokeWidth=2;';
        }
        if (this.mode === 'simulation') {
            return 'strokeColor=#f59c24;dashed=1;';
        } else {
            return 'strokeColor=#59A1FF;';
        }
    }

    drawNextFlowChart(
        actiDefId: string,
        nodeDict: { [key: string]: DiagramElement; },
        edgeDict: { [BpmnElementId: string]: DiagramElement; },
    ) {
        const { nextFlowNodeDict, nextSeqFlowDict } = this.findDrawFlowElementsByActDefId(actiDefId, this.bpmnModel);
        const actDef = this.bpmnModel.DefaultProcess.GetFlowElement(actiDefId) as FlowNode;
        const outgoings = actDef.Outgoings;

        // 先插入节点
        Object.keys(nextFlowNodeDict).forEach(id => {
            const flowElt = nextFlowNodeDict[id];
            const diagElt = nodeDict[flowElt.Id] as BPMNShape;
            const cell = this.getCellByFlowElementId(flowElt.Id);
            if (!cell) {
                this.drawNodeCell(flowElt, diagElt, true);
            }
        });

        // 再插入边
        Object.keys(nextSeqFlowDict).forEach(id => {
            const flowElt = nextSeqFlowDict[id];
            const diagElt = edgeDict[flowElt.Id] as BPMNEdge;
            const cell = this.getCellByFlowElementId(flowElt.Id);
            if (!cell) {
                if (outgoings.some(o => o === flowElt.Id)) {
                    this.drawEdgeCell(flowElt, diagElt, true, true);
                } else {
                    this.drawEdgeCell(flowElt, diagElt, true, false);
                }
            }
        });
    }

    /**
     * 根据flowElementId获取cell
     */
    getCellByFlowElementId(flowEltId: string, parent?: MxGraphNS.mxCell): Cell {
        parent = parent ? parent : this.getDefaultParent();
        const cells = this.getChildCells(parent);
        const cell = cells.find(it => {
            if (it instanceof Cell) {
                return it.flowElement && it.flowElement.Id == flowEltId;
            } else {
                return false;
            }
        }) as Cell;
        return cell;
    }

    findFirstStartActivityInstance(activities: ActivityInstanceSlim[]): ActivityInstanceSlim {
        for (const act of activities) {
            if (!act.precursorActivityDefinitionId) {
                return act;
            }
        }
        return null;
    }

    findNoIncomingNodes(nodeCells: Cell[]) {
        const noIncomingNodes = new Array();
        for (const node of nodeCells) {
            if (node.flowElement.ClrTypeID === 'wf.StartActivity') {
                continue;
            }
            if (!node.edges || node.edges.length === 0) {
                noIncomingNodes.push(node);
                continue;
            }
            if (!node.edges.some(edge => (edge.target as Cell).flowElement.Id === node.flowElement.Id)) {
                noIncomingNodes.push(node);
            }
        }
        return noIncomingNodes;
    }

    findEndActiInstList(firstAct: ActivityInstanceSlim, activities: ActivityInstanceSlim[], transInsList: TransitionInstanceSlim[]): ActivityInstanceSlim[] {
        const endActiInstList: ActivityInstanceSlim[] = [];
        this.getNextTransitions(endActiInstList, firstAct.activityInstanceId, activities, transInsList);
        return endActiInstList;
    }

    getNextTransitions(endActiInstList: ActivityInstanceSlim[], sourceActInstId: string, activities: ActivityInstanceSlim[], transInsList: TransitionInstanceSlim[]) {
        const nextTransitions = this.findTranInstsBySourceActInstId(sourceActInstId, transInsList);
        if (nextTransitions && nextTransitions.length > 0) {
            for (const t of nextTransitions) {
                if (t.destinationActivityDefinitionId) {
                    this.getNextTransitions(endActiInstList, t.destinationActivityInstanceId, activities, transInsList);
                }
            }
        } else {
            const actInst: ActivityInstanceSlim = this.findActInstByActInstId(sourceActInstId, activities);
            if (actInst) {
                if (!endActiInstList.some(a => a.activityInstanceId === actInst.activityInstanceId)) {
                    endActiInstList.push(actInst);
                }
            }
        }
    }

    findTranInstsBySourceActInstId(sourceActInstId: string, transInsList: TransitionInstanceSlim[]): TransitionInstanceSlim[] {
        return transInsList.filter(t => t.sourceActivityInstanceId === sourceActInstId);
    }

    findActInstByActInstId(actInstId: string, activities: ActivityInstanceSlim[]) {
        return activities.find(a => a.activityInstanceId === actInstId);
    }

    findDrawFlowElementsByActDefId(actiDefId: string, bpmnModel: BpmnModel) {
        const nextFlowNodeDict: { [id: string]: FlowNode } = {};
        const nextSeqFlowDict: { [id: string]: SequenceFlow } = {};
        this.findNextFlowElementsByFlowElementId(nextFlowNodeDict, nextSeqFlowDict, actiDefId, bpmnModel);
        return {
            nextFlowNodeDict,
            nextSeqFlowDict,
        };
    }

    findNextFlowElementsByFlowElementId(nextFlowNodeDict: { [id: string]: FlowNode }, nextSeqFlowDict: { [id: string]: SequenceFlow }, flowElementId: string, bpmnModel: BpmnModel) {
        const flowElt = bpmnModel.DefaultProcess.GetFlowElement(flowElementId);
        if (flowElt && nextFlowNodeDict[flowElementId] == null) {
            if (flowElt instanceof SequenceFlow) {
                nextSeqFlowDict[flowElt.Id] = flowElt;
                this.findNextFlowElementsByFlowElementId(nextFlowNodeDict, nextSeqFlowDict, flowElt.TargetRef, bpmnModel);
            } else if (flowElt instanceof FlowNode) {
                nextFlowNodeDict[flowElt.Id] = flowElt;
                if ((flowElt instanceof EndEvent) == false) {
                    flowElt.Outgoings && flowElt.Outgoings.forEach(outgoing => {
                        this.findNextFlowElementsByFlowElementId(nextFlowNodeDict, nextSeqFlowDict, outgoing, bpmnModel);
                    });
                }
            }
        }
    }
}
